import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { UPDATE_ACCOUNT_IDENTITY_MODAL } from '../../../../constants/modals';
import useOpenModal from '../../../../hooks/useOpenModal';
import {
  onSelectIdentity,
  onDeselectIdentity,
} from '../../../../actions/discover-album-folder';
import IdentityAvatarContainer from '../../../../containers/global/identity/IdentityAvatarContainer';
import { AVATAR_SIZE_52 } from '../../../../constants/avatar-sizes';
import EditIcon from '../../../../components/icons/EditIcon';
import IdentityNameContainer from '../../../../containers/global/identity/IdentityNameContainer';
import Badge from '../../../../components/global/Badge';
import CustomControl from '../../../../components/global/CustomControl';
import { getUploadedPhotoByIdentity } from '../../../../util/photo-upload';

const propTypes = {
  identity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    current_photo: PropTypes.object,
    has_outdated_selfie: PropTypes.bool,
  }),
  isSelected: PropTypes.bool,
  isPurchased: PropTypes.bool,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  isJoined: PropTypes.bool,
};

const JoinAlbumFolderIdentity = ({
  identity,
  isSelected,
  isPurchased,
  isJoined,
  albumFolder,
}) => {
  const openModal = useOpenModal();
  const dispatch = useDispatch();

  const onSelectIdentityCallback = useCallback(
    (...args) => dispatch(onSelectIdentity(...args)),
    [dispatch],
  );
  const onDeselectIdentityCallback = useCallback(
    (...args) => dispatch(onDeselectIdentity(...args)),
    [dispatch],
  );

  const { uploadedPhoto } = useSelector(({ photoUploads }) => ({
    uploadedPhoto: getUploadedPhotoByIdentity(photoUploads.photos, identity),
  }));

  const onEditIdentity = event => {
    event.preventDefault();
    event.stopPropagation();

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
      forceSelfieUpdate:
        (!identity.current_photo || identity.has_outdated_selfie) &&
        !uploadedPhoto,
      outdatedSelfie: identity.has_outdated_selfie && !uploadedPhoto,
    });
  };

  const onIdentityToggle = event => {
    event.preventDefault();

    if (isPurchased) {
      return;
    }

    if (!identity.current_photo && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        forceSelfieUpdate: true,
      });

      return;
    }

    // `updateSelfie` request is not sync in nature, i.e.
    // the field identity.has_outdated_selfie will return `true`
    // even if we update the selfie, so to check if a user
    // really has an outdated selfie we also need to check
    // our local redux store for recent uploads for this identity,
    // if there's an `uploadedPhoto` it means that the user just
    // uploaded a selfie (which by definition is not considered outdated)
    if (identity.has_outdated_selfie && !uploadedPhoto) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
        outdatedSelfie: true,
      });
      return;
    }

    if (!identity.last_name) {
      openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
        identity,
      });
      return;
    }

    if (isSelected) {
      onDeselectIdentityCallback(identity);
    } else {
      onSelectIdentityCallback({
        ...identity,
        selectedAlbums: [],
        albumFolders: [albumFolder],
      });
    }
  };

  return (
    <a
      href="#"
      className="identity-container"
      data-testid="identity-container"
      onClick={onIdentityToggle}
    >
      <IdentityAvatarContainer
        identity={identity}
        className="mr-2"
        size={AVATAR_SIZE_52}
        onClick={onEditIdentity}
      >
        <span className="btn-identity-edit">
          <EditIcon color="#0d84fe" />
        </span>
      </IdentityAvatarContainer>

      <IdentityNameContainer
        className="text-dark mr-1"
        identity={identity}
        defaultName="Find photos of me"
      />
      {!isPurchased && isJoined && (
        <span className="text-primary">(Joined)</span>
      )}
      {isPurchased && <Badge className="ml-auto">Subscribed</Badge>}
      {!isPurchased && (
        <div className="ml-auto">
          <CustomControl
            id={identity.id}
            type="checkbox"
            size="xl"
            checked={isSelected}
            readOnly
          />
        </div>
      )}
    </a>
  );
};

JoinAlbumFolderIdentity.propTypes = propTypes;

export default JoinAlbumFolderIdentity;
